import { Base, withMountWhenInView } from '@studiometa/js-toolkit';
import type {
  BaseConfig,
  BaseProps,
  RafServiceProps,
  ScrollServiceProps,
} from '@studiometa/js-toolkit';

export interface CanvasSphereProps extends BaseProps {
  $el: HTMLCanvasElement;
  $options: {
    /**
     * Amount of dots on the screen
     */
    dotsAmount: number;
    /**
     * Radius of the dots
     */
    dotRadius: number;
  };
}

type Dot = Record<'x' | 'y' | 'z', number>;

export default class CanvasSphere extends withMountWhenInView(Base)<CanvasSphereProps> {
  static config: BaseConfig = {
    name: 'CanvasSphere',
    options: {
      dotsAmount: {
        type: Number,
        default: 1200,
      },
      dotRadius: {
        type: Number,
        default: 1,
      },
    },
  };

  props = {
    width: 0,
    height: 0,
    radius: 0,
    centerZ: 0,
    centerX: 0,
    centerY: 0,
    fieldOfView: 0,
  };

  ctx: CanvasRenderingContext2D;

  timeLast = 0; // Last time of the animation
  timeDelta = 0; // Delta time of the animation
  scrollDelta = 0; // Delta Y of the scroll

  rotation = 0; // Rotation of the globe

  /**
   * Every dots in an array
   */
  dots: Dot[] = [];

  mounted() {
    const ctx = this.$el.getContext('2d');
    if (!ctx) {
      this.$warn('Failed to get context from root element');
      return this.$destroy();
    }

    this.ctx = ctx;
    this.resized();
  }

  updateProps() {
    const { clientWidth, clientHeight } = this.$el;
    this.props.width = clientWidth;
    this.props.height = clientHeight;
    this.props.radius = clientWidth * 0.7;
    this.props.centerZ = this.props.radius * -1;
    this.props.centerX = clientWidth / 2;
    this.props.centerY = (clientHeight / 3) * 2;
    this.props.fieldOfView = clientWidth / 2;
  }

  createDots() {
    // Empty the array of dots
    this.dots.length = 0;

    // Create a new dot based on the amount needed
    for (let i = 0; i < this.$options.dotsAmount; i++) {
      const theta = Math.random() * 2 * Math.PI; // Random value between [0, 2PI]
      const phi = Math.acos(Math.random() * 2 - 1); // Random value between [-1, 1]

      // Calculate the [x, y, z] coordinates of the dot along the globe
      const x = this.props.radius * Math.sin(phi) * Math.cos(theta);
      const y = this.props.radius * Math.sin(phi) * Math.sin(theta);
      const z = this.props.radius * Math.cos(phi) - this.props.radius;
      this.dots.push({ x, y, z });
    }
  }

  drawDot({ x, y, z }: Dot, sin: number, cos: number, dotRadius: number) {
    // Do some math to project the 3D position into the 2D canvas
    const rotX = cos * x + sin * (z - this.props.centerZ);
    const rotZ = -sin * x + cos * (z - this.props.centerZ) + this.props.centerZ;
    const sizeProjection = this.props.fieldOfView / (this.props.fieldOfView - rotZ);
    const xProject = rotX * sizeProjection + this.props.centerX;
    const yProject = y * sizeProjection + this.props.centerY;

    // Draw the dot on the canvas
    this.ctx.beginPath();
    this.ctx.arc(xProject, yProject, dotRadius * sizeProjection, 0, Math.PI * 2);
    this.ctx.closePath();
    this.ctx.fillStyle = '#E9D7FE';
    this.ctx.fill();
  }

  ticked({ time }: RafServiceProps) {
    if (!this.ctx) {
      return;
    }

    // Clear the scene
    this.ctx.clearRect(0, 0, this.props.width, this.props.height);

    // Increase the globe rotation
    this.timeDelta = time - this.timeLast;
    this.timeLast = time;
    this.rotation = this.rotation + this.timeDelta * 0.0001 + Math.abs(this.scrollDelta) * 0.0005;

    const sineRotation = Math.sin(this.rotation); // Sine of the rotation
    const cosineRotation = Math.cos(this.rotation); // Cosine of the rotation
    const { dotRadius } = this.$options;

    // Loop through the dots array and draw every dot
    for (let i = 0; i < this.dots.length; i++) {
      this.drawDot(this.dots[i], sineRotation, cosineRotation, dotRadius);
    }
  }

  /**
   * Update delta scroll on scroll.
   */
  scrolled({ deltaY }: ScrollServiceProps) {
    this.scrollDelta = deltaY;
  }

  resized() {
    if (
      (this.$el.clientWidth === this.props.width ||
        (this.$el.clientWidth * 2 === this.props.width && window.devicePixelRatio > 1)) &&
      (this.$el.clientHeight === this.props.height ||
        (this.$el.clientHeight * 2 === this.props.height && window.devicePixelRatio > 1))
    ) {
      return;
    }

    this.updateProps();

    if (window.devicePixelRatio > 1) {
      this.$el.width = this.props.width * 2;
      this.$el.height = this.props.height * 2;
      this.ctx?.scale(2, 2);
    }

    // Populate the dots array with random dots
    this.createDots();
  }
}
